import React, { useEffect, useRef, useState } from "react";
import { useButton } from "react-aria";
import { useAnimation } from "framer-motion";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
`;

const UploadContainer = styled(motion.div)`
  width: 100%;
  cursor: pointer;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
`;

const UploadBox = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  border: 2px dashed ${props => props.$isDragOver ? '#1a73e8' : '#d1d5db'};
  border-radius: 16px;
  background: ${props => props.$isDragOver 
    ? 'linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 100%)' 
    : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'};
  transition: all 0.3s ease;
  gap: 16px;

  &:hover {
    border-color: #1a73e8;
    background: linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 100%);
    
    .upload-icon-wrapper {
      transform: scale(1.05);
      box-shadow: 0 8px 24px rgba(26, 115, 232, 0.25);
    }
    
    .upload-arrow {
      animation: ${float} 1.5s ease-in-out infinite;
    }
  }
`;

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(26, 115, 232, 0.2);
`;

const UploadText = styled.div`
  text-align: center;
`;

const MainText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
`;

const SubText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`;

const SupportedFormats = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const FormatBadge = styled.span`
  padding: 4px 10px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
`;

const SuccessUploadBox = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border: 2px solid #10b981;
  border-radius: 16px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  gap: 16px;
`;

const SuccessIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SuccessContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuccessText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #065f46;
  margin: 0 0 2px 0;
`;

const FileName = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: #059669;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChangeButton = styled.button`
  padding: 8px 16px;
  background: white;
  border: 1px solid #10b981;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #059669;
    color: white;
  }
`;

// SVG Icons
const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      className="upload-arrow"
      d="M12 16V4M12 4L8 8M12 4L16 8" 
      stroke="white" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M20 16V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V16" 
      stroke="white" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 13L9 17L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ButtonUploadPayment = ({ onClick, status, uploadedFile }) => {
  const ref = useRef();
  const [isDragOver, setIsDragOver] = useState(false);

  const { buttonProps } = useButton(
    {
      onPress: onClick,
    },
    ref
  );

  // Determine the message based on the status prop
  const messages = {
    uploadedMessage: {
      main: "Upload Payment Proof",
      sub: "Click or drag & drop your receipt here"
    },
    otherMessage: {
      main: "上傳付款證明",
      sub: "點擊或拖放收據到此處"
    },
  };

  const content = messages[status] || messages.uploadedMessage;

  // If a file has been uploaded, show success state
  if (uploadedFile) {
    return (
      <UploadContainer ref={ref} {...buttonProps}>
        <SuccessUploadBox
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <SuccessIconWrapper>
            <CheckIcon />
          </SuccessIconWrapper>
          <SuccessContent>
            <SuccessText>Payment proof uploaded!</SuccessText>
            <FileName>{uploadedFile.name || "Receipt uploaded"}</FileName>
          </SuccessContent>
          <ChangeButton type="button">Change</ChangeButton>
        </SuccessUploadBox>
      </UploadContainer>
    );
  }

  return (
    <UploadContainer 
      ref={ref} 
      {...buttonProps}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
      }}
    >
      <UploadBox 
        $isDragOver={isDragOver}
        whileTap={{ scale: 0.98 }}
      >
        <IconWrapper className="upload-icon-wrapper">
          <UploadIcon />
        </IconWrapper>
        <UploadText>
          <MainText>{content.main}</MainText>
          <SubText>{content.sub}</SubText>
        </UploadText>
        <SupportedFormats>
          <FormatBadge>JPG</FormatBadge>
          <FormatBadge>PNG</FormatBadge>
          <FormatBadge>PDF</FormatBadge>
        </SupportedFormats>
      </UploadBox>
    </UploadContainer>
  );
};

export default ButtonUploadPayment;

// Export legacy components for backward compatibility
export const AddIcon = () => (
  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM16.25 12.75H12.75V16.25C12.75 16.6645 12.414 17 12 17C11.586 17 11.25 16.6645 11.25 16.25V12.75H7.75C7.336 12.75 7 12.4145 7 12C7 11.5855 7.336 11.25 7.75 11.25H11.25V7.75C11.25 7.3355 11.586 7 12 7C12.414 7 12.75 7.3355 12.75 7.75V11.25H16.25C16.664 11.25 17 11.5855 17 12C17 12.4145 16.664 12.75 16.25 12.75Z" fill="#637394"/>
  </svg>
);

export const CameraIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 20C6.93125 20 4.14375 13.2675 8.08125 9.33125C12.0188 5.395 18.75 8.18125 18.75 13.75C18.75 15.4076 18.0915 16.9973 16.9194 18.1694C15.7473 19.3415 14.1576 20 12.5 20ZM8.025 3.2L7.1875 4C6.725 4.44625 6.10625 5.01 5.4625 5.0125H2.5C1.1425 5 0 5.89 0 7.2V20.3C0 21.5875 1.25 22.5 2.5 22.5H22.5C23.75 22.5 25 21.605 25 20.3V7.2C25 5.90375 23.75 5 22.5 5H19.5375C18.8937 4.9975 18.275 4.43375 17.8125 3.9875L16.975 3.1875C16.5096 2.74536 15.8919 2.4992 15.25 2.5H9.75C9.10633 2.50257 8.48844 2.7533 8.025 3.2Z" fill="#334B7B" fillOpacity="0.7"/>
    <path d="M12.5 17.5C14.5711 17.5 16.25 15.8211 16.25 13.75C16.25 11.6789 14.5711 10 12.5 10C10.4289 10 8.75 11.6789 8.75 13.75C8.75 15.8211 10.4289 17.5 12.5 17.5Z" fill="#334B7B" fillOpacity="0.7"/>
  </svg>
);

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 80%;
  height: 90px;
  padding: 0 10px;
  border-radius: 10px;
`;

export const TitleMessage = styled.h1`
  font: 400 18px Lora, sans-serif;
  text-align: center;
  margin-bottom: 0px;
  color: #0c729e;
  font-style: italic;
  padding: 0 10px;
`;

export const MergedButtonWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 80%;
  height: 90px;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  background-color: "#657F80";
`;
